import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react"
import useCurrentPeriod from "hooks/useCurrentPeriod"
import useObjectiveNavigation from "hooks/useObjectiveNavigation"
import useCurrentNode from "hooks/useCurrentNode"
import useRequest from "hooks/useRequest"
import EnvCollectEntity from "services/entities/EnvCollectEntity"
import SocialCollectEntity from "services/entities/SocialCollectEntity"
import ScoreEntity from "services/entities/ScoreEntity"
import ProfitCollectEntity from "../services/entities/ProfitCollectEntity"
import SubscriptionEntity from "services/entities/SubscriptionEntity"
import PropTypes from "prop-types"
import { useEntity } from "hooks/useAxios"
import CountryEntity from "../services/entities/CountryEntity"
import UserEntity from "../services/entities/UserEntity"
import useAuth0 from "./auth/useAuth0"
import UserStateEntity from "services/entities/UserStateEntity"
import HistorizationChartEntity from "services/entities/HistorizationChartEntity"
import useDisabledFeatures from "hooks/useDisabledFeatures"
import useRedirection from "hooks/useRedirection"
import { TOAST_STATUS_INACTIVE } from "utils/constants/toastStatuses"
import ProductEntity from "services/entities/ProductEntity"
import useSentry from "hooks/useSentry"
import OrganizationEntity from "services/entities/OrganizationEntity"
import { markIsCurrentNodeBelow } from "components/widgets/Dashboard/OrganizationChart/ChartContext"
import { useTreeFilters } from "hooks/useTreeFilters"
import { useTreeSort } from "hooks/useTreeSort"
import useProductFruits from "hooks/useProductFruits"

export const AppContext = createContext(null)

const useTreeExpandedState = (currentRootNodeId) => {
  const [treeExpandedState, setTreeExpandedState] = useState(new Set())
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (!currentRootNodeId || isInitialized) return

    try {
      const stored = localStorage.getItem("tree_expanded_state")
      if (stored) {
        const parsed = JSON.parse(stored)

        if (parsed.rootNodeId === currentRootNodeId) {
          setTreeExpandedState(new Set(parsed.nodes || []))
        } else {
          setTreeExpandedState(new Set())
        }
      } else {
        setTreeExpandedState(new Set())
      }
      setIsInitialized(true)
    } catch {
      setTreeExpandedState(new Set())
      setIsInitialized(true)
    }
  }, [currentRootNodeId, isInitialized])

  useEffect(() => {
    if (!currentRootNodeId || !isInitialized) return

    const timeoutId = setTimeout(() => {
      try {
        const nodes = [...treeExpandedState]
        localStorage.setItem(
          "tree_expanded_state",
          JSON.stringify({
            nodes,
            rootNodeId: currentRootNodeId,
          })
        )
      } catch (error) {
        console.error("[TreeState] Failed to save:", error)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [treeExpandedState, currentRootNodeId, isInitialized])

  const toggleTreeExpandedState = useCallback((nodeId) => {
    setTreeExpandedState((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }, [])

  const setTreeNodeExpanded = useCallback((nodeId, expanded) => {
    setTreeExpandedState((prev) => {
      const newSet = new Set(prev)
      if (expanded) {
        newSet.add(nodeId)
      } else {
        newSet.delete(nodeId)
      }
      return newSet
    })
  }, [])

  const isTreeNodeExpanded = useCallback(
    (nodeId) => {
      return treeExpandedState.has(nodeId)
    },
    [treeExpandedState]
  )

  const clearTreeExpandedState = useCallback(() => {
    localStorage.removeItem("tree_expanded_state")
    setTreeExpandedState(new Set())
  }, [])

  return {
    treeExpandedState,
    toggleTreeExpandedState,
    setTreeNodeExpanded,
    isTreeNodeExpanded,
    clearTreeExpandedState,
  }
}

export const AppContextProvider = ({ children }) => {
  const { isAuthenticated } = useAuth0()
  const [hasAccessRights, setHasAccessRights] = useState(true)

  const [shouldRefreshNotifications, setShouldRefreshNotifications] =
    useState(true)
  const [toastStatus, setToastStatus] = useState(TOAST_STATUS_INACTIVE)

  const envCollectEntity = useEntity(EnvCollectEntity)
  const socialCollectEntity = useEntity(SocialCollectEntity)
  const scoreEntity = useEntity(ScoreEntity)
  const userEntity = useEntity(UserEntity)
  const profitCollectEntity = useEntity(ProfitCollectEntity)
  const subscriptionEntity = useEntity(SubscriptionEntity)
  const countryEntity = useEntity(CountryEntity)
  const userStateEntity = useEntity(UserStateEntity)
  const historizationChartEntity = useEntity(HistorizationChartEntity)
  const productEntity = useEntity(ProductEntity)
  const organizationEntity = useEntity(OrganizationEntity)

  const {
    data: currentUser,
    setData: setCurrentUser,
    reload: reloadCurrentUser,
    errorCode: userErrorCode,
    isLoading: isCurrentUserLoading,
  } = useRequest(() => userEntity.show())

  useEffect(() => {
    if (userErrorCode === 401) {
      setHasAccessRights(false)
    }
  }, [userErrorCode])

  // Record one login per authenticated session opening (Auth0 flow).
  // Devise local login is handled by a Warden hook on the backend.
  const loginRecordedRef = useRef(false)
  useEffect(() => {
    if (!isAuthenticated || loginRecordedRef.current) return
    loginRecordedRef.current = true
    userEntity.recordLogin().catch(() => {})
  }, [isAuthenticated])

  const {
    data: currentRootNode,
    setData: setCurrentRootNode,
    reload: reloadCurrentRoot,
    isLoading: isCurrentRootNodeLoading,
  } = useRequest(() => userStateEntity.getCurrentRoot())

  const { data: productDetails } = useRequest(
    () => productEntity.index(),
    [currentRootNode]
  )

  const { data: subscription } = useRequest(
    () => subscriptionEntity.current(),
    [currentRootNode],
    { condition: !!currentRootNode }
  )

  const { currentPeriod, reload: reloadCurrentPeriod } = useCurrentPeriod(
    [subscription, currentRootNode],
    !!subscription && !subscription.is_provider
  )

  const {
    data: currentOrganization,
    isLoading: isCurrentOrganizationLoading,
    reload: reloadCurrentOrganization,
  } = useRequest(() => userStateEntity.getOrganization(), [currentRootNode])

  const {
    pillarNavigation,
    objectiveNavigation,
    updateObjectiveNavigation,
    objectiveUri,
  } = useObjectiveNavigation()

  const {
    filters: treeFilters,
    pendingFilters,
    updatePendingFilter,
    applyFilters,
    setOnlyFilter: setOnlyTreeFilter,
    setOnlyNodeFilter: setOnlyTreeNodeFilter,
    resetFilters: resetTreeFilters,
    hasActiveFilters,
    hasPendingChanges,
  } = useTreeFilters()

  const {
    currentNode,
    updateCurrentNode,
    isCurrentNodeLoading,
    parentOrganization,
    isParentOrganizationLoading,
    reloadCurrentNode,
  } = useCurrentNode([currentRootNode], !!currentRootNode)

  const { treeSort, updateTreeSort, clearTreeSort } = useTreeSort()

  const {
    treeExpandedState,
    toggleTreeExpandedState,
    setTreeNodeExpanded,
    isTreeNodeExpanded,
    clearTreeExpandedState,
  } = useTreeExpandedState(currentRootNode?.id)

  const treeForceReloadRef = useRef(false)

  const {
    data: tree,
    reload: reloadTreeBase,
    isLoading: isTreeLoading,
  } = useRequest(
    () => {
      const forceReload = treeForceReloadRef.current
      treeForceReloadRef.current = false

      return organizationEntity
        .getCurrentOrganization(treeFilters, treeSort, {
          reload: forceReload,
        })
        .catch((err) => {
          if (err.response?.status === 403) setHasAccessRights(false)
          throw err
        })
    },
    [
      currentRootNode,
      currentNode,
      treeFilters.scored,
      treeFilters.open,
      treeFilters.guests,
      treeFilters.search,
      treeFilters.selectedNodeIds?.join(","),
      treeSort.field,
      treeSort.direction,
    ],
    {
      condition: !!currentRootNode,
      getter: ({ data }) => {
        markIsCurrentNodeBelow(data, treeFilters.selectedNodeIds)
        return data
      },
    }
  )

  const reloadTree = useCallback(() => {
    treeForceReloadRef.current = true
    reloadTreeBase()
  }, [reloadTreeBase])

  useEffect(() => {
    if (currentRootNode?.id) {
      resetTreeFilters()
      clearTreeSort()
      clearTreeExpandedState()
    }
  }, [
    currentRootNode?.id,
    resetTreeFilters,
    clearTreeSort,
    clearTreeExpandedState,
  ])

  const {
    data: envCollect,
    setData: setEnvCollect,
    isLoading: isEnvCollectLoading,
    reload: reloadEnvCollect,
  } = useRequest(() => envCollectEntity.show(), [currentNode, currentPeriod], {
    condition: !!currentNode,
  })

  const {
    data: socialCollect,
    setData: setSocialCollect,
    isLoading: isSocialCollectLoading,
    reload: reloadSocialCollect,
  } = useRequest(
    () => socialCollectEntity.show(),
    [currentNode, currentPeriod],
    {
      condition: !!currentNode,
    }
  )

  const profitCollectRequest = useRequest(
    () => profitCollectEntity.show(),
    [currentNode, currentPeriod],
    {
      condition: !!currentNode,
    }
  )
  const profitCollect = profitCollectRequest.data

  const {
    data: score,
    setData: setScore,
    isLoading: isScoreLoading,
    reload: reloadScore,
  } = useRequest(() => scoreEntity.getScore(), [currentNode], {
    condition: !!currentNode,
  })

  const {
    data: envScore,
    isLoading: isEnvScoreLoading,
    isError: isEnvScoreError,
    reload: reloadEnvScore,
  } = useRequest(() => scoreEntity.getEnvironmentalScore(), [currentNode], {
    condition: !!currentNode,
  })

  const {
    data: socialScore,
    isLoading: isSocialScoreLoading,
    isError: isSocialScoreError,
    reload: reloadSocialScore,
  } = useRequest(() => scoreEntity.getSocialScore(), [currentNode], {
    condition: !!currentNode,
  })

  const profitScoreRequest = useRequest(
    () => scoreEntity.getProfitScore(),
    [currentNode],
    {
      condition: !!currentNode,
    }
  )
  const profitScore = profitScoreRequest.data
  const isProfitScoreLoading = profitScoreRequest.isLoading

  const { data: availableProducts, reload: reloadProducts } = useRequest(() =>
    userEntity.availableProducts()
  )

  const { data: countryOptions } = useRequest(
    () => countryEntity.getOptions(),
    [],
    { getter: ({ data }) => data.alpha2s }
  )

  const historizationRequest = useRequest(
    () => historizationChartEntity.chart(),
    [currentPeriod, currentNode, score],
    { condition: !!score }
  )

  const disabledFeatures = useDisabledFeatures({
    isAuthenticated,
    isCurrentRootNodeLoading,
    currentNode,
    isCurrentNodeLoading,
    score,
    isScoreLoading,
    envScore,
    isEnvScoreLoading,
    historizationRequest,
    currentOrganization,
    isCurrentOrganizationLoading,
    profitScoreRequest,
    socialScore,
    isSocialScoreLoading,
    currentRootNode,
  })

  const redirect = useRedirection({ disabledFeatures, score, currentNode })

  useSentry({
    currentUser,
    currentOrganization,
    currentRootNode,
    currentNode,
  })

  useProductFruits(currentUser, isCurrentUserLoading)

  return (
    <AppContext.Provider
      value={{
        isAuthenticated,
        hasAccessRights,
        currentUser,
        setCurrentUser,
        reloadCurrentUser,
        isCurrentUserLoading,
        toastStatus,
        setToastStatus,
        currentRootNode,
        isCurrentRootNodeLoading,
        reloadCurrentRoot,
        currentOrganization,
        reloadCurrentOrganization,
        currentNode,
        reloadCurrentNode,
        updateCurrentNode,
        isCurrentNodeLoading,
        setCurrentRootNode,
        shouldRefreshNotifications,
        setShouldRefreshNotifications,
        currentPeriod,
        reloadCurrentPeriod,
        objectiveNavigation,
        updateObjectiveNavigation,
        pillarNavigation,
        objectiveUri,
        parentOrganization,
        isParentOrganizationLoading,
        envCollect,
        setEnvCollect,
        isEnvCollectLoading,
        reloadEnvCollect,
        socialCollect,
        setSocialCollect,
        isSocialCollectLoading,
        reloadSocialCollect,
        profitCollect,
        profitCollectRequest,
        score,
        setScore,
        reloadScore,
        isScoreLoading,
        envScore,
        reloadEnvScore,
        isEnvScoreLoading,
        isEnvScoreError,
        socialScore,
        reloadSocialScore,
        isSocialScoreLoading,
        isSocialScoreError,
        availableProducts,
        reloadProducts,
        subscription,
        profitScore,
        profitScoreRequest,
        isProfitScoreLoading,
        countryOptions,
        historizationRequest,
        disabledFeatures,
        redirect,
        productDetails,
        tree,
        reloadTree,
        isTreeLoading,
        setHasAccessRights,
        treeFilters,
        pendingFilters,
        updatePendingFilter,
        applyFilters,
        setOnlyTreeFilter,
        setOnlyTreeNodeFilter,
        resetTreeFilters,
        hasActiveFilters,
        hasPendingChanges,
        treeSort,
        updateTreeSort,
        clearTreeSort,
        treeExpandedState,
        toggleTreeExpandedState,
        setTreeNodeExpanded,
        isTreeNodeExpanded,
        clearTreeExpandedState,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function useAppContext() {
  return useContext(AppContext)
}
